import { Download, TableChartOutlined } from '@mui/icons-material';
import {
    Box,
    IconButton,
    Menu,
    TableCell as MuiTableCell,
    styled,
    Table,
    TableBody,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
} from '@mui/material';
import { format as formatDate } from 'date-fns';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useLocalStorage } from 'usehooks-ts';
import { CheckboxMenuItem } from '~/components/Menu';
import { SegmentationData } from '~/hooks/workflows/backend/tasks/useSegmentationMetrics';
import { saveBlobToFile } from '~/utils/io/files/downloadDicom';
import {
    DEFAULT_VISIBLE_COLUMN_IDS,
    NOT_AVAILABLE,
    SEGMETRICS_COLUMNS,
    TOGGLEABLE_COLUMNS,
} from './segmetricsColumns';
import { buildSegmetricsCsv } from './segmetricsCsv';

const VISIBLE_COLUMNS_STORAGE_KEY = 'SEGMETRICS_VISIBLE_COLUMNS';

type SegmentationTableProps = {
    data: SegmentationData[];
};

const TableCell = styled(MuiTableCell)(({ theme }) => ({
    fontSize: 12,
    padding: '6px 12px',
    borderBottom: `1px solid ${theme.palette.divider}`,
}));

const HeaderCell = styled(TableCell)({
    fontSize: 11,
    lineHeight: 1.5,
    whiteSpace: 'nowrap',
});

const SegmentationTable = ({ data }: SegmentationTableProps) => {
    const intl = useIntl();
    const [storedColumnIds, setVisibleColumnIds] = useLocalStorage<string[]>(
        VISIBLE_COLUMNS_STORAGE_KEY,
        DEFAULT_VISIBLE_COLUMN_IDS
    );
    // A poisoned/legacy persisted value may not be an array
    const visibleColumnIds = Array.isArray(storedColumnIds)
        ? storedColumnIds
        : DEFAULT_VISIBLE_COLUMN_IDS;
    const [columnMenuAnchor, setColumnMenuAnchor] =
        useState<HTMLElement | null>(null);

    // Unknown stored ids are ignored
    const visibleColumns = SEGMETRICS_COLUMNS.filter(
        (column) => column.alwaysVisible || visibleColumnIds.includes(column.id)
    );

    const toggleColumn = (columnId: string) => {
        setVisibleColumnIds(
            visibleColumnIds.includes(columnId)
                ? visibleColumnIds.filter((id) => id !== columnId)
                : [...visibleColumnIds, columnId]
        );
    };

    const downloadCsv = () => {
        // Export always includes every metric, regardless of visibility
        const csv = buildSegmetricsCsv(data, SEGMETRICS_COLUMNS, (column) =>
            intl.formatMessage({ id: column.labelId })
        );
        // Local wall-clock time: a UTC timestamp can carry the wrong
        // calendar date for the clinician filing the export
        const timestamp = formatDate(new Date(), "yyyy-MM-dd'T'HH-mm-ss");
        saveBlobToFile(
            // BOM so Excel on Windows decodes UTF-8 (cm³, accented names)
            new Blob(['\uFEFF', csv], { type: 'text/csv;charset=utf-8' }),
            `segmetrics_${timestamp}.csv`
        );
    };

    const columnsLabel = intl.formatMessage({ id: 'columns' });
    const downloadCsvLabel = intl.formatMessage({
        id: 'segmetrics.downloadCsv',
    });

    return (
        <TableContainer
            sx={(theme) => ({
                maxHeight: 200,
                overflow: 'auto',
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: `${theme.shape.borderRadius}px`,
            })}
        >
            <Table stickyHeader>
                <TableHead>
                    <TableRow>
                        {visibleColumns.map((column) => (
                            <HeaderCell key={column.id}>
                                {intl.formatMessage({ id: column.labelId })}
                            </HeaderCell>
                        ))}
                        <HeaderCell
                            align="right"
                            // Pinned corner: stays visible during horizontal
                            // scroll, above the other sticky header cells
                            sx={{
                                py: 0,
                                width: '1%',
                                position: 'sticky',
                                right: 0,
                                zIndex: 3,
                            }}
                        >
                            <Box
                                display="flex"
                                justifyContent="flex-end"
                                gap={0.25}
                            >
                                <Tooltip title={columnsLabel} placement="left">
                                    <IconButton
                                        size="small"
                                        aria-label={columnsLabel}
                                        aria-haspopup="true"
                                        aria-expanded={
                                            columnMenuAnchor ? true : undefined
                                        }
                                        aria-controls={
                                            columnMenuAnchor
                                                ? 'segmetrics-columns-menu'
                                                : undefined
                                        }
                                        onClick={(event) =>
                                            setColumnMenuAnchor(
                                                event.currentTarget
                                            )
                                        }
                                    >
                                        <TableChartOutlined
                                            sx={{ fontSize: 16 }}
                                        />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title={downloadCsvLabel}>
                                    <IconButton
                                        size="small"
                                        aria-label={downloadCsvLabel}
                                        onClick={downloadCsv}
                                    >
                                        <Download sx={{ fontSize: 16 }} />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </HeaderCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {data.map((item, index) => (
                        // ROI names are not guaranteed unique in DICOM, so
                        // the name alone is not a safe key
                        <TableRow key={`${index}-${item.gt_roi_name}`}>
                            {visibleColumns.map((column) => (
                                <TableCell key={column.id}>
                                    {column.format(item) ?? NOT_AVAILABLE}
                                </TableCell>
                            ))}
                            <TableCell />
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            <Menu
                id="segmetrics-columns-menu"
                anchorEl={columnMenuAnchor}
                open={Boolean(columnMenuAnchor)}
                onClose={() => setColumnMenuAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{ paper: { sx: { maxHeight: 320 } } }}
            >
                {TOGGLEABLE_COLUMNS.map((column) => (
                    <CheckboxMenuItem
                        key={column.id}
                        checked={visibleColumnIds.includes(column.id)}
                        label={intl.formatMessage({ id: column.labelId })}
                        onClick={() => toggleColumn(column.id)}
                    />
                ))}
            </Menu>
        </TableContainer>
    );
};

export default SegmentationTable;
